import styled from 'styled-components';

export const Content = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
  display: grid;
  gap: 24px;
`;

export const Panel = styled.section`
  background: #fff;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.xl};
  box-shadow: ${(p) => p.theme.shadows.sm};
  padding: 26px 28px;
  min-width: 0;
  box-sizing: border-box;

  font-size: ${(p) => p.theme.fontSizes.base};
  > p { color: ${(p) => p.theme.colors.gray400}; font-size: ${(p) => p.theme.fontSizes.sm}; line-height: 1.5; }

  @media (max-width: 639px) {
    padding: 20px 16px;
    border-radius: ${(p) => p.theme.radii.lg};
  }
`;

export const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

export const SectionHeading = styled.h2`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.md};
  font-weight: 700;
  line-height: 1.4;
  text-wrap: balance;
`;

export const UploadRow = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 14px;
  align-items: center;
  margin-top: 18px;

  > button {
    min-height: 46px;
  }

  .error {
    grid-column: 1 / -1;
    width: 100%;
    color: ${(p) => p.theme.colors.danger};
    margin: 0;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;

    > button {
      width: 100%;
    }
  }
`;

export const FilePicker = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-width: 0;
  min-height: 72px;
  padding: 14px 16px;
  border: 1px dashed ${(p) => p.theme.colors.primary200};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.gray000};
  opacity: ${(p) => (p.$disabled ? 0.7 : 1)};
  transition: border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease;

  &:focus-within {
    border-color: ${(p) => p.theme.colors.primary500};
    background: #f8faff;
    box-shadow: 0 0 0 3px rgba(11, 49, 157, 0.14);
  }

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const UploadCopy = styled.div`
  min-width: 0;
`;

export const UploadLabel = styled.span`
  display: block;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: 700;
`;

export const SelectedFileName = styled.span`
  display: block;
  margin-top: 5px;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.sm};
  overflow-wrap: anywhere;
`;

export const BrowseLabel = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${(p) => p.theme.colors.primary500};
  border-radius: ${(p) => p.theme.radii.base};
  background: ${(p) => p.theme.colors.light};
  color: ${(p) => p.theme.colors.primary500};
  font-size: ${(p) => p.theme.fontSizes.sm};
  font-weight: 700;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};

  &:hover {
    background: ${(p) => (p.$disabled ? p.theme.colors.light : '#eef3ff')};
  }

  &:focus-within {
    outline: 3px solid ${(p) => p.theme.colors.primary300};
    outline-offset: 3px;
  }

  @media (max-width: 520px) {
    width: 100%;
    box-sizing: border-box;
  }
`;

export const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const StatsPanel = styled.section`
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.xl};
  background: ${(p) => p.theme.colors.light};
  box-shadow: ${(p) => p.theme.shadows.sm};
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Stat = styled.div`
  min-width: 0;
  padding: 19px 22px;
  border-right: 1px solid ${(p) => p.theme.colors.gray100};

  &:last-child {
    border-right: 0;
  }

  span {
    display: block;
    color: ${(p) => p.theme.colors.gray400};
    font-size: ${(p) => p.theme.fontSizes.sm};
    font-weight: 600;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: ${(p) => p.theme.colors.primary500};
    font-size: ${(p) => p.theme.fontSizes.md};
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }

  @media (max-width: 900px) {
    &:nth-child(2) {
      border-right: 0;
    }

    &:nth-child(-n + 2) {
      border-bottom: 1px solid ${(p) => p.theme.colors.gray100};
    }
  }

`;

export const Toolbar = styled.form`
  display: grid;
  grid-template-columns: minmax(140px, 0.8fr) minmax(180px, 1fr) minmax(140px, 0.8fr) minmax(240px, 1.6fr) auto;
  gap: 14px;
  align-items: flex-end;
  padding: 18px;
  border: 1px solid ${(p) => p.theme.colors.gray100};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.gray000};

  label {
    display: grid;
    min-width: 0;
    gap: 7px;
    color: ${(p) => p.theme.colors.gray500};
    font-size: ${(p) => p.theme.fontSizes.sm};
    font-weight: 700;
  }

  select,
  input {
    width: 100%;
    min-width: 0;
    min-height: 44px;
    padding: 8px 11px;
    border: 1px solid ${(p) => p.theme.colors.gray200};
    border-radius: ${(p) => p.theme.radii.base};
    background: ${(p) => p.theme.colors.light};
    color: ${(p) => p.theme.colors.gray500};
    box-sizing: border-box;
    font: inherit;
    font-weight: 400;
  }

  select:focus-visible,
  input:focus-visible {
    border-color: ${(p) => p.theme.colors.primary500};
    outline: 3px solid rgba(11, 49, 157, 0.16);
    outline-offset: 1px;
  }

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .search {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding: 14px;

    .search {
      grid-column: auto;
    }
  }
`;

export const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;

  > button {
    min-height: 44px;
  }

  @media (max-width: 1180px) {
    grid-column: 1 / -1;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > button {
      width: 100%;
      padding-inline: 12px;
    }
  }
`;

export const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  border-radius: 999px;
  padding: 4px 9px;
  font-size: ${(p) => p.theme.fontSizes.xs};
  color: ${(p) => (p.$status === 'failed' || p.$status === 'result_unknown' ? '#7a1a1a' : '#17336c')};
  background: ${(p) => (p.$status === 'confirmed' ? '#e5f6df' : p.$status === 'failed' || p.$status === 'result_unknown' ? '#ffe4e4' : '#eef1f8')};
`;

export const IntegrityBadge = styled.span`
  display: inline-block;
  border-radius: 999px;
  padding: 4px 9px;
  background: #e5f6df;
  color: #23511c;
  font-size: ${(p) => p.theme.fontSizes.xs};
  font-weight: 600;
`;

export const ActionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  button, a { min-height: 34px; padding: 6px 9px; border: 1px solid ${(p) => p.theme.colors.primary500}; border-radius: ${(p) => p.theme.radii.base}; background: #fff; color: ${(p) => p.theme.colors.primary500}; cursor: pointer; text-decoration: none; font-size: ${(p) => p.theme.fontSizes.sm}; }

  button:hover,
  a:hover {
    background: #eef3ff;
  }

  button:focus-visible,
  a:focus-visible {
    outline: 3px solid ${(p) => p.theme.colors.primary300};
    outline-offset: 2px;
  }
`;

export const VerifyLabel = styled.label`
  min-height: 34px;
  padding: 6px 9px;
  border: 1px solid ${(p) => p.theme.colors.primary500};
  color: ${(p) => p.theme.colors.primary500};
  cursor: pointer;
  box-sizing: border-box;
  font-size: ${(p) => p.theme.fontSizes.sm};
  input { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

  &:hover {
    background: #eef3ff;
  }

  &:focus-within {
    outline: 3px solid ${(p) => p.theme.colors.primary300};
    outline-offset: 2px;
  }
`;

export const Notice = styled.div`
  border: 1px solid #c5dcf7;
  border-left: 4px solid ${(p) => p.theme.colors.info};
  border-radius: ${(p) => p.theme.radii.base};
  background: #f5f9ff;
  padding: 14px 16px;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.sm};
  line-height: 1.55;
`;

export const TableArea = styled.div`
  margin-top: 22px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  contain: inline-size layout paint;

  > div {
    min-width: 0;
    max-width: 100%;
  }

  table th {
    padding: 13px 12px !important;
    background: ${(p) => p.theme.colors.gray000};
    color: ${(p) => p.theme.colors.gray400} !important;
    font-size: ${(p) => p.theme.fontSizes.sm};
  }

  table td {
    padding: 15px 12px !important;
  }
`;

export const EmptyState = styled.div`
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 52px 24px;
  border: 1px dashed ${(p) => p.theme.colors.gray200};
  border-radius: ${(p) => p.theme.radii.lg};
  background: ${(p) => p.theme.colors.gray000};
  text-align: center;

  > button {
    margin-top: 8px;
  }
`;

export const EmptyTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.colors.gray500};
  font-size: ${(p) => p.theme.fontSizes.md};
`;

export const EmptyCopy = styled.p`
  max-width: 560px;
  margin: 0;
  color: ${(p) => p.theme.colors.gray400};
  font-size: ${(p) => p.theme.fontSizes.sm};
  line-height: 1.55;
`;
